/**
 * "New report" — demand capture, and nothing else (POS2-1137 CP1).
 *
 * No builder, no promised date. The mock's version is a toast that goes nowhere; the spec says the
 * note "lands with CS", and there is no CS inbox in this platform (findings F14). Of the three
 * ways to give it one — an email through the existing sender, an activity row, a webhook — the
 * email is the only one that needs no system that does not exist: `activity.record` is POS2-1138's
 * and is not built yet. So it POSTs to `/reports/requests`, which mails the address already
 * configured as `EMAIL_REPLY_TO`.
 *
 * The site goes with it. A request that does not say which kitchen it came from is one CS has to
 * write back about, and the page already knows.
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { apiError, showApiError } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** Long enough to be a sentence. The server holds the same floor. */
const MIN_LENGTH = 10;

export function NewReportDialog({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const [note, setNote] = useState("");

  const request = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/reports/requests`, {
        method: "POST",
        headers,
        body: JSON.stringify({ site_id: siteId, note: note.trim() }),
      });
      if (!res.ok) throw await apiError(t, res);
    },
    onSuccess: () => {
      toast.success(t("reports.request.sent"));
      onClose();
    },
    onError: showApiError,
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("reports.request.title")}</DialogTitle>
          <DialogDescription>{t("reports.request.description")}</DialogDescription>
        </DialogHeader>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("reports.request.placeholder")}
          rows={4}
          aria-label={t("reports.request.title")}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => request.mutate()}
            disabled={note.trim().length < MIN_LENGTH || request.isPending}
          >
            {t("reports.request.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
